export interface SpatialPoint {
  x: number
  y: number
  z: number
}

export interface SpatialObstacle {
  id: string
  centerX: number
  centerZ: number
  halfWidth: number
  halfDepth: number
  yaw: number
  minY: number
  maxY: number
}

export interface BodyResolution {
  position: SpatialPoint
  collided: boolean
  obstacleIds: string[]
}

export interface SegmentClipResult {
  point: SpatialPoint
  blocked: boolean
  obstacleId: string | null
  hitFraction: number
}

const EPSILON = 1e-8

export function resolveBodyAgainstObstacles(
  position: SpatialPoint,
  radius: number,
  height: number,
  obstacles: readonly SpatialObstacle[],
): BodyResolution {
  const resolved = { ...position }
  const collidedIds = new Set<string>()
  const safeRadius = Math.max(0, radius)
  const safeHeight = Math.max(0, height)

  // A few passes settle corners where one correction can enter a neighboring box.
  for (let pass = 0; pass < 4; pass += 1) {
    let corrected = false
    for (const obstacle of obstacles) {
      if (resolved.y >= obstacle.maxY || resolved.y + safeHeight <= obstacle.minY) continue
      const local = worldToLocal(resolved, obstacle)
      const closestX = clamp(local.x, -obstacle.halfWidth, obstacle.halfWidth)
      const closestZ = clamp(local.z, -obstacle.halfDepth, obstacle.halfDepth)
      const offsetX = local.x - closestX
      const offsetZ = local.z - closestZ
      const distanceSquared = offsetX * offsetX + offsetZ * offsetZ

      let correctionX = 0
      let correctionZ = 0
      if (distanceSquared > EPSILON && distanceSquared < safeRadius * safeRadius) {
        const distance = Math.sqrt(distanceSquared)
        const push = safeRadius - distance
        correctionX = (offsetX / distance) * push
        correctionZ = (offsetZ / distance) * push
      } else if (
        Math.abs(local.x) <= obstacle.halfWidth &&
        Math.abs(local.z) <= obstacle.halfDepth
      ) {
        const pushX = obstacle.halfWidth + safeRadius - Math.abs(local.x)
        const pushZ = obstacle.halfDepth + safeRadius - Math.abs(local.z)
        if (pushX <= pushZ) correctionX = signedDirection(local.x) * pushX
        else correctionZ = signedDirection(local.z) * pushZ
      }

      if (correctionX === 0 && correctionZ === 0) continue
      const worldCorrection = localVectorToWorld(correctionX, correctionZ, obstacle.yaw)
      resolved.x += worldCorrection.x
      resolved.z += worldCorrection.z
      collidedIds.add(obstacle.id)
      corrected = true
    }
    if (!corrected) break
  }

  return {
    position: resolved,
    collided: collidedIds.size > 0,
    obstacleIds: [...collidedIds],
  }
}

export function hasLineOfSight(
  start: SpatialPoint,
  end: SpatialPoint,
  obstacles: readonly SpatialObstacle[],
  clearance = 0,
): boolean {
  return firstObstacleHitFraction(start, end, obstacles, clearance).obstacleId === null
}

export function clipSegmentToObstacles(
  start: SpatialPoint,
  end: SpatialPoint,
  obstacles: readonly SpatialObstacle[],
  clearance = 0,
  padding = 0,
): SegmentClipResult {
  const hit = firstObstacleHitFraction(start, end, obstacles, clearance)
  if (hit.obstacleId === null) {
    return { point: { ...end }, blocked: false, obstacleId: null, hitFraction: 1 }
  }

  const deltaX = end.x - start.x
  const deltaY = end.y - start.y
  const deltaZ = end.z - start.z
  const distance = Math.hypot(deltaX, deltaY, deltaZ)
  const safeFraction = Math.max(0, hit.fraction - Math.max(0, padding) / Math.max(EPSILON, distance))
  return {
    point: {
      x: start.x + deltaX * safeFraction,
      y: start.y + deltaY * safeFraction,
      z: start.z + deltaZ * safeFraction,
    },
    blocked: true,
    obstacleId: hit.obstacleId,
    hitFraction: hit.fraction,
  }
}

function firstObstacleHitFraction(
  start: SpatialPoint,
  end: SpatialPoint,
  obstacles: readonly SpatialObstacle[],
  clearance: number,
): { obstacleId: string | null; fraction: number } {
  let obstacleId: string | null = null
  let fraction = 1
  for (const obstacle of obstacles) {
    const candidate = segmentObstacleHitFraction(start, end, obstacle, Math.max(0, clearance))
    if (candidate === null || candidate >= fraction) continue
    fraction = candidate
    obstacleId = obstacle.id
  }
  return { obstacleId, fraction }
}

function segmentObstacleHitFraction(
  start: SpatialPoint,
  end: SpatialPoint,
  obstacle: SpatialObstacle,
  clearance: number,
): number | null {
  const localStart = worldToLocal(start, obstacle)
  const localEnd = worldToLocal(end, obstacle)
  let minimum = 0
  let maximum = 1

  const axes: ReadonlyArray<readonly [number, number, number, number]> = [
    [localStart.x, localEnd.x - localStart.x, -obstacle.halfWidth - clearance, obstacle.halfWidth + clearance],
    [start.y, end.y - start.y, obstacle.minY - clearance, obstacle.maxY + clearance],
    [localStart.z, localEnd.z - localStart.z, -obstacle.halfDepth - clearance, obstacle.halfDepth + clearance],
  ]

  for (const [origin, delta, lower, upper] of axes) {
    if (Math.abs(delta) <= EPSILON) {
      if (origin < lower || origin > upper) return null
      continue
    }
    const first = (lower - origin) / delta
    const second = (upper - origin) / delta
    const near = Math.min(first, second)
    const far = Math.max(first, second)
    minimum = Math.max(minimum, near)
    maximum = Math.min(maximum, far)
    if (minimum > maximum) return null
  }

  return minimum >= 0 && minimum <= 1 ? minimum : null
}

function worldToLocal(point: SpatialPoint, obstacle: SpatialObstacle): { x: number; z: number } {
  const deltaX = point.x - obstacle.centerX
  const deltaZ = point.z - obstacle.centerZ
  const cosine = Math.cos(obstacle.yaw)
  const sine = Math.sin(obstacle.yaw)
  return {
    x: cosine * deltaX - sine * deltaZ,
    z: sine * deltaX + cosine * deltaZ,
  }
}

function localVectorToWorld(x: number, z: number, yaw: number): { x: number; z: number } {
  const cosine = Math.cos(yaw)
  const sine = Math.sin(yaw)
  return {
    x: cosine * x + sine * z,
    z: -sine * x + cosine * z,
  }
}

function signedDirection(value: number): number {
  return value < 0 ? -1 : 1
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value))
}
